// ParamedicalDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import { useCart } from '../../../context/CartContext';

const ParamedicalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { cart, addParamedicalService, addMedicine, isAuthenticated } = useCart();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addingMedicineId, setAddingMedicineId] = useState(null);
  const [relatedServices, setRelatedServices] = useState([]);
  const [suggestedMedicines, setSuggestedMedicines] = useState([]);
  const [suggestedDoctors, setSuggestedDoctors] = useState([]);
  const [suggestedTests, setSuggestedTests] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);

  // Helper function to get medicine quantity from cart
  const getMedicineQtyInCart = (medicineId) => {
    if (!cart || !cart.medicine_items) return 0;
    const item = cart.medicine_items.find(i => i.medicine?.id === medicineId);
    return item ? item.quantity : 0;
  };

  // Handle add medicine to cart from suggestions
  const handleAddMedicineToCart = async (e, medicineId) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    setAddingMedicineId(medicineId);
    try {
      await addMedicine(medicineId, 1);
    } catch (error) {
      console.error('Error adding medicine:', error);
      alert(error.message || 'Failed to add medicine');
    } finally {
      setAddingMedicineId(null);
    }
  };

  useEffect(() => {
    // Fetch service details
    api.get(`/paramedical-services/${id}/`)
      .then(res => {
        // Fix: Access nested data from API response
        if (res.data.success) {
          setService(res.data.data);
        } else {
          setService(res.data); // Fallback for different response format
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching service:', err);
        setLoading(false);
      });

    // Fetch related services using dedicated API
    api.get(`/suggestions/related-services/${id}/`)
      .then(res => {
        if (res.data.success) {
          setRelatedServices(res.data.related_services || []);
        }
      })
      .catch(err => {
        console.error('Error fetching related services:', err);
        setRelatedServices([]);
      });

    // Fetch suggestions from actual API
    setLoadingSuggestions(true);
    api.get(`/suggestions/for-paramedical-service/${id}/`)
      .then(res => {
        if (res.data.success) {
          // Set medicines
          setSuggestedMedicines(res.data.suggested_medicines || []);

          // Set doctors
          setSuggestedDoctors(res.data.suggested_doctors || []);

          // Set tests
          setSuggestedTests(res.data.suggested_tests || []);
        }
        setLoadingSuggestions(false);
      })
      .catch(err => {
        console.error('Error fetching suggestions:', err);
        setLoadingSuggestions(false);
        // Keep empty arrays on error
        setSuggestedMedicines([]);
        setSuggestedDoctors([]);
        setSuggestedTests([]);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-lg p-12 max-w-md mx-auto">
          <div className="text-gray-400 text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Service Not Found</h3>
          <p className="text-gray-500 mb-6">The requested service could not be found.</p>
          <Link 
            to="/paramedical" 
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
        <Header />
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="shadow-sm border-b" style={{ backgroundColor: '#104a5f' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-white opacity-80 hover:opacity-100 text-sm">
                  Home
                </Link>
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 text-white opacity-60 mx-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <Link to="/paramedical" className="text-white opacity-80 hover:opacity-100 text-sm">
                  Paramedical Services
                </Link>
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 text-white opacity-60 mx-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-white font-medium text-sm">{service.title}</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border">
          {/* Hero Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Service Image */}
            <div className="relative h-96 lg:h-auto overflow-hidden bg-gray-100">
              {service.image ? (
                <img
                  src={`https://www.homecureexpress.com${service.image}`}
                  alt={service.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}

              {/* Placeholder */}
              <div
                className={`w-full h-full bg-gradient-to-br from-teal-50 to-cyan-100 flex items-center justify-center ${service.image ? 'hidden' : 'flex'}`}
                style={service.image ? { display: 'none' } : {}}
              >
                <div className="text-center">
                  <div className="text-9xl mb-4">🩺</div>
                  <p className="text-teal-700 font-semibold text-xl">Paramedical Service</p>
                </div>
              </div>

              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col space-y-2">
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Available
                </span>
                {service.offer_price && parseFloat(service.offer_price) < parseFloat(service.price) && (
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                    {Math.round((1 - parseFloat(service.offer_price) / parseFloat(service.price)) * 100)}% OFF
                  </span>
                )}
              </div>
            </div>

            {/* Service Info */}
            <div className="p-8 lg:p-10">
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{service.title}</h1>

              {/* Rating & Reviews (Mock) */}
              <div className="flex items-center mb-6 pb-6 border-b">
                <div className="flex items-center mr-4">
                  {[1, 2, 3, 4, 5].map(star => (
                    <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
                <span className="text-gray-600 text-sm">4.8 (250+ reviews)</span>
              </div>

              {/* Pricing */}
              <div className="mb-8">
                <div className="flex items-baseline space-x-3 mb-2">
                  {service.offer_price && parseFloat(service.offer_price) < parseFloat(service.price) ? (
                    <>
                      <span className="text-4xl font-bold text-green-600">₹{service.offer_price}</span>
                      <span className="text-2xl text-gray-400 line-through">₹{service.price}</span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold text-gray-900">₹{service.price}</span>
                  )}
                </div>
                <p className="text-sm text-gray-500">Inclusive of all taxes • No hidden charges</p>
              </div>

              {/* Quick Features */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>Home Service</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>24/7 Available</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Verified Professionals</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Safe & Hygienic</span>
                </div>
              </div>

              {/* CTA Button */}
              <Link
                to={`/paramedical/book/${service.id}`}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 flex items-center justify-center group shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Book Appointment Now
                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>

              <p className="text-xs text-gray-500 text-center mt-4">
                🔒 100% Secure Payment • ⚡ Instant Booking Confirmation
              </p>
            </div>
          </div>

          {/* Description Section */}
          <div className="border-t p-8 lg:p-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">About This Service</h2>
            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">{service.description}</p>
            </div>

            {/* Key Features */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: '✓', title: 'Certified Professionals', color: 'green' },
                { icon: '⏰', title: '24/7 Availability', color: 'blue' },
                { icon: '🏥', title: 'Advanced Equipment', color: 'purple' },
                { icon: '🔒', title: 'Safe & Sterile', color: 'orange' }
              ].map((feature, idx) => (
                <div key={idx} className={`bg-${feature.color}-50 border border-${feature.color}-100 rounded-lg p-4`}>
                  <div className={`text-3xl mb-2 text-${feature.color}-600`}>{feature.icon}</div>
                  <p className={`font-medium text-${feature.color}-900`}>{feature.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Suggested Services Section */}
        {relatedServices.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Related Services</h2>
              <Link to="/paramedical" className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center">
                View All
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedServices.map(relService => (
                <Link
                  key={relService.id}
                  to={`/paramedical/${relService.id}`}
                  className="bg-white rounded-lg border hover:shadow-lg transition-shadow duration-200 overflow-hidden group"
                >
                  <div className="h-40 bg-gray-100 overflow-hidden">
                    {relService.image ? (
                      <img
                        src={`https://www.homecureexpress.com${relService.image}`}
                        alt={relService.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50"><span class="text-5xl">🩺</span></div>'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50">
                        <span className="text-5xl">🩺</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{relService.title}</h3>
                    <div className="flex items-baseline space-x-2">
                      {relService.offer_price && parseFloat(relService.offer_price) < parseFloat(relService.price) ? (
                        <>
                          <span className="text-lg font-bold text-teal-600">₹{relService.offer_price}</span>
                          <span className="text-sm text-gray-400 line-through">₹{relService.price}</span>
                        </>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">₹{relService.price}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Medicines Section */}
        {loadingSuggestions ? (
          <div className="mt-12">
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading suggestions...</p>
            </div>
          </div>
        ) : suggestedMedicines.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Recommended Medicines & Supplies</h2>
                <p className="text-sm text-gray-600 mt-1">Commonly needed for this service</p>
              </div>
              <Link to="/medicines" className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center">
                View All
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
              {suggestedMedicines.slice(0, 8).map(medicine => {
                const qty = getMedicineQtyInCart(medicine.id);
                const isAdding = addingMedicineId === medicine.id;
                const discount = medicine.offer_price && parseFloat(medicine.offer_price) < parseFloat(medicine.mrp)
                  ? Math.round(((parseFloat(medicine.mrp) - parseFloat(medicine.offer_price)) / parseFloat(medicine.mrp)) * 100)
                  : 0;

                return (
                  <div key={medicine.id} className="bg-white rounded-lg border hover:shadow-lg transition-all duration-200 overflow-hidden group relative">
                    {/* Discount Badge */}
                    {discount > 0 && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full z-10 font-semibold">
                        {discount}% OFF
                      </div>
                    )}

                    {/* Medicine Image */}
                    <Link to={`/medicine/${medicine.id}`}>
                      <div className="h-24 bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center overflow-hidden p-2">
                        {medicine.image ? (
                          <img
                            src={medicine.image}
                            alt={medicine.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full flex items-center justify-center ${medicine.image ? 'hidden' : 'flex'}`}>
                          <span className="text-4xl">💊</span>
                        </div>
                      </div>
                    </Link>

                    <div className="p-3">
                      {/* Medicine Name */}
                      <Link to={`/medicine/${medicine.id}`} className="no-underline">
                        <h3 className="font-semibold text-gray-900 text-xs mb-2 line-clamp-2 h-8 hover:text-teal-600">{medicine.name}</h3>
                      </Link>

                      {/* Pricing */}
                      <div className="mb-2">
                        <div className="flex items-baseline gap-1">
                          <span className="text-sm font-bold text-teal-600">₹{medicine.offer_price}</span>
                          {discount > 0 && (
                            <span className="text-xs text-gray-400 line-through">₹{medicine.mrp}</span>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      {medicine.in_stock ? (
                        qty > 0 ? (
                          <div className="flex flex-col gap-1.5">
                            <div className="text-xs text-green-600 font-semibold text-center">
                              {qty} in Cart
                            </div>
                            <Link
                              to="/cart"
                              onClick={(e) => e.stopPropagation()}
                              className="w-full bg-green-600 text-white text-xs py-1.5 rounded hover:bg-green-700 transition-colors font-medium text-center no-underline"
                            >
                              View Cart
                            </Link>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => handleAddMedicineToCart(e, medicine.id)}
                            disabled={isAdding}
                            className="w-full bg-teal-600 text-white text-xs py-1.5 rounded hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isAdding ? 'Adding...' : 'Add to Cart'}
                          </button>
                        )
                      ) : (
                        <span className="block text-center text-xs text-red-600 font-medium py-1.5">Out of Stock</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Suggested Doctors Section */}
        {!loadingSuggestions && suggestedDoctors.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Consult Related Specialists</h2>
                <p className="text-sm text-gray-600 mt-1">Expert doctors for consultation</p>
              </div>
              <Link to="/doctors" className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center">
                View All Doctors
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {suggestedDoctors.slice(0, 6).map(doctor => (
                <div key={doctor.id} className="bg-white rounded-lg border hover:shadow-lg transition-shadow duration-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      {/* Doctor Profile Picture */}
                      <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-teal-100 to-cyan-100">
                        {doctor.profile_image ? (
                          <img
                            src={doctor.profile_image}
                            alt={`Dr. ${doctor.name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full flex items-center justify-center ${doctor.profile_image ? 'hidden' : 'flex'}`}>
                          <span className="text-3xl">👨‍⚕️</span>
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 mb-1">Dr. {doctor.name}</h3>
                        <p className="text-sm text-teal-600 font-medium mb-1">{doctor.specialization}</p>
                        <p className="text-xs text-gray-500">{doctor.experience_years} years experience</p>
                        {doctor.is_available_now && (
                          <span className="inline-flex items-center mt-2 text-xs text-green-600">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                            Available
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Bio */}
                    {doctor.bio && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{doctor.bio}</p>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        {/* Rating */}
                        <div className="flex items-center mb-1">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-4 h-4 ${i < Math.floor(parseFloat(doctor.average_rating)) ? 'text-yellow-400' : 'text-gray-300'}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                            </svg>
                          ))}
                          <span className="ml-1 text-xs text-gray-600">{doctor.average_rating}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">₹{doctor.price}</p>
                      </div>
                      <Link
                        to={`/doctor/${doctor.id}`}
                        className="bg-teal-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-teal-700 transition-colors"
                      >
                        Consult Now
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Tests Section */}
        {!loadingSuggestions && suggestedTests.length > 0 && (
          <div className="mt-12 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Recommended Lab Tests</h2>
                <p className="text-sm text-gray-600 mt-1">Diagnostic tests for better care</p>
              </div>
              <Link to="/lab-tests" className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center">
                View All Tests
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {suggestedTests.slice(0, 6).map(test => (
                <div key={test.id} className="bg-white rounded-lg border hover:shadow-lg transition-shadow duration-200 overflow-hidden">
                  {/* Test Icon */}
                  <div className="h-32 bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
                    <span className="text-6xl">🔬</span>
                  </div>

                  <div className="p-5">
                    {/* Test Name */}
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{test.title}</h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{test.description}</p>

                    {/* Features */}
                    <div className="space-y-2 mb-4">
                      {test.report_time && (
                        <div className="flex items-center text-xs text-gray-600">
                          <svg className="w-4 h-4 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Report in {test.report_time}
                        </div>
                      )}
                      {test.is_home_collection_available && (
                        <div className="flex items-center text-xs text-green-600 font-medium">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Home Collection Available
                        </div>
                      )}
                      <div className="flex items-center text-xs text-green-600 font-medium">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Free Home Collection
                      </div>
                    </div>

                    {/* Pricing & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        {test.offer_price && parseFloat(test.offer_price) < parseFloat(test.price) ? (
                          <div>
                            <span className="text-lg font-bold text-teal-600">₹{test.offer_price}</span>
                            <span className="text-sm text-gray-400 line-through ml-2">₹{test.price}</span>
                          </div>
                        ) : (
                          <span className="text-lg font-bold text-gray-900">₹{test.price}</span>
                        )}
                      </div>
                      <Link
                        to={`/medical-test/${test.id}`}
                        className="text-xs bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors font-medium no-underline"
                      >
                        Book Test
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    <Footer />
      </>
  );
};

export default ParamedicalDetail;